import cn from "classnames";
import { MediaType } from "constants/enums";
import { useBoolean } from "hooks";
import { DirectUploadMedia } from "libs/directUpload";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { LightBoxGallery } from "ui";

import {
  EXPEDITION_CONFIRMATION_SOURCE_LABEL,
  ORDER_STATUSES_LIST,
  OrderStatus
} from "../../../../../constants";
import { OrderHistory } from "../../../../../orders.types";
import { OrderAtomProps } from "../../OrderModal.types";
import { OrderModalBlock } from "./OrderModalBlock";

import styles from "../../OrderModal.module.scss";

const buildReceiptMedia = (historyItem: OrderHistory): DirectUploadMedia => ({
  id: historyItem.mediaId ?? historyItem.id,
  url: historyItem.mediaUrl ?? "",
  key: "",
  fileName: "expedition-receipt",
  mediaType: MediaType.IMAGE,
  contentType: "image/jpeg",
  size: 0
});

export const OrderTrack = ({ orderDetails }: OrderAtomProps) => {
  const { t } = useTranslation();
  const {
    value: isReceiptOpen,
    setTrue: openReceipt,
    setFalse: closeReceipt
  } = useBoolean(false);
  const getOrderStatusLabel = (status: string) => {
    if (status === OrderStatus.Placed) return "Order Placed";
    return (
      ORDER_STATUSES_LIST.find((orderStatus) => orderStatus.value === status)
        ?.label ?? status
    );
  };

  const isBadStatus = (status: string) =>
    status === OrderStatus.Failed || status === OrderStatus.Cancelled;

  const expeditionReceiptItem = orderDetails.history.find(
    (item) => item.status === OrderStatus.Expedited && item.mediaUrl
  );

  return (
    <OrderModalBlock label={t("Track Order")}>
      <div className={styles.orderModal__trackBlock}>
        {orderDetails.history.map((historyItem) => {
          const confirmationSourceLabel =
            historyItem.status === OrderStatus.Delivered &&
            orderDetails.isExpedition &&
            historyItem.confirmationSource
              ? EXPEDITION_CONFIRMATION_SOURCE_LABEL[
                  historyItem.confirmationSource
                ]
              : null;

          return (
            <div
              className={styles.orderModal__trackBlock__item}
              key={`history-${historyItem.id}`}
            >
              <div
                className={cn(
                  styles.orderModal__trackBlock__point,
                  isBadStatus(historyItem.status) &&
                    styles.orderModal__trackBlock__pointRed
                )}
              />
              <p>{t(getOrderStatusLabel(historyItem.status))}</p>
              {historyItem.explanation && (
                <p className={styles.orderModal__trackBlock__description}>
                  {t(historyItem.explanation)}
                </p>
              )}
              {historyItem.status === OrderStatus.Expedited &&
                historyItem.mediaUrl && (
                  <img
                    src={historyItem.mediaUrl}
                    alt={t("Expedition receipt")}
                    className={styles.orderModal__trackBlock__receiptThumbnail}
                    onClick={openReceipt}
                  />
                )}
              {confirmationSourceLabel && (
                <p className={styles.orderModal__trackBlock__confirmationSource}>
                  {t(confirmationSourceLabel)}
                </p>
              )}
              <p className={styles.orderModal__trackBlock__date}>
                {`${moment(historyItem.createdAt).format("dddd, DD MMMM ")} at ${moment(historyItem.createdAt).format("HH:mm")}`}
              </p>
            </div>
          );
        })}
        {orderDetails.history.length <= 0 && (
          <div className={styles.emptyDataMessage}>
            {t("There are no history")}
          </div>
        )}
      </div>
      {expeditionReceiptItem && (
        <LightBoxGallery
          mediaList={[buildReceiptMedia(expeditionReceiptItem)]}
          openedImageIndex={isReceiptOpen ? 0 : undefined}
          setOpenedImageIndex={(index) =>
            index === undefined ? closeReceipt() : openReceipt()
          }
        />
      )}
    </OrderModalBlock>
  );
};
